import React from 'react';
import { Check, Star, Zap, Crown } from 'lucide-react';
import { Plano } from '../../types/checkout';

interface PlanoSelectorProps {
  onSelect: (plano: Plano) => void;
  planoSelecionado?: Plano | null;
}

const PlanoSelector: React.FC<PlanoSelectorProps> = ({ onSelect, planoSelecionado }) => {
  const planos: Plano[] = [
    {
      id: 'teste',
      nome: 'Teste',
      preco: 6, // R$ 6,00 para teste (mínimo do Asaas)
      descricao: '1 empresa • 5 funcionários • 7 dias',
      tipo: 'BASICO',
      funcionalidades: [
        'Cronograma automático NR-05',
        'Sistema de votação online',
        'Relatórios básicos',
        'Membro designado',
        '1 empresa apenas',
        'Até 5 funcionários',
        'Suporte por email',
        'Válido por 7 dias'
      ]
    },
    {
      id: 'basico',
      nome: 'Básico',
      preco: 97, // R$ 97/mês (R$ 1.164/ano)
      descricao: '3 empresas • 140 funcionários no total',
      tipo: 'BASICO',
      funcionalidades: [
        'Cronograma automático NR-05',
        'Sistema de votação online',
        'Relatórios de conformidade',
        'Membro designado',
        'Até 3 empresas',
        'Até 140 funcionários total',
        'Suporte por email',
        'Backup semanal'
      ]
    },
    {
      id: 'profissional',
      nome: 'Profissional',
      preco: 297, // R$ 297/mês (R$ 3.564/ano)
      descricao: '10 empresas • 250 eleitores e funcionários',
      tipo: 'PROFISSIONAL',
      funcionalidades: [
        'Cronograma automático NR-05',
        'Sistema de votação online',
        'Relatórios avançados',
        'Membro designado',
        'Até 10 empresas',
        'Até 250 eleitores e funcionários',
        'Suporte prioritário',
        'Backup diário'
      ]
    },
    {
      id: 'enterprise',
      nome: 'Enterprise',
      preco: 497, // R$ 497/mês (R$ 5.964/ano)
      descricao: '25 empresas • Acesso ilimitado',
      tipo: 'ENTERPRISE',
      funcionalidades: [
        'Cronograma automático NR-05',
        'Votação eletrônica',
        'Relatórios avançados',
        'Membro designado',
        'Ata Digital',
        'Plano de Trabalho',
        'Cronograma de Reuniões',
        'Gestão de Plano de Ação',
        'Até 25 empresas',
        'Funcionários ilimitados',
        'Suporte dedicado'
      ]
    }
  ];

  const getIcon = (tipo: string) => {
    switch (tipo) {
      case 'TESTE':
        return <Check className="w-6 h-6 text-green-500" />;
      case 'BASICO':
        return <Zap className="w-6 h-6 text-blue-500" />;
      case 'PROFISSIONAL':
        return <Star className="w-6 h-6 text-purple-500" />;
      case 'ENTERPRISE':
        return <Crown className="w-6 h-6 text-yellow-500" />;
      default:
        return <Zap className="w-6 h-6 text-blue-500" />;
    }
  };

  const getBadge = (tipo: string) => {
    switch (tipo) {
      case 'TESTE':
        return <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">Para Teste</span>;
      case 'BASICO':
        return <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">Ideal para Pequenas</span>;
      case 'PROFISSIONAL':
        return <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded">Mais Popular</span>;
      case 'ENTERPRISE':
        return <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded">Completo</span>;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Escolha seu Plano
        </h1>
        <p className="text-lg text-gray-600">
          Selecione o plano ideal para sua empresa e comece a usar o CIPA Fácil EHS
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {planos.map((plano) => (
          <div
            key={plano.id}
            className={`relative bg-white rounded-lg shadow-lg border-2 transition-all duration-300 hover:shadow-xl cursor-pointer ${
              planoSelecionado?.id === plano.id
                ? 'border-blue-500 ring-2 ring-blue-200'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => onSelect(plano)}
          >
            {/* Badge */}
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              {getBadge(plano.tipo)}
            </div>

            <div className="p-6">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  {getIcon(plano.tipo)}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {plano.nome}
                </h3>
                <p className="text-gray-600 mb-4">
                  {plano.descricao}
                </p>
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900">
                    R$ {plano.preco}
                    <span className="text-sm font-normal text-gray-500">/mês</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    R$ {plano.preco * 12}/ano
                  </div>
                  <div className="text-xs text-green-600 mt-1 font-medium">
                    💳 Parcelável em até 12x sem juros
                  </div>
                </div>
              </div>

              {/* Funcionalidades */}
              <div className="space-y-3 mb-6">
                {plano.funcionalidades.map((funcionalidade, index) => (
                  <div key={index} className="flex items-start">
                    <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">
                      {funcionalidade}
                    </span>
                  </div>
                ))}
              </div>

              {/* Botão de seleção */}
              <button
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                  planoSelecionado?.id === plano.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {planoSelecionado?.id === plano.id ? 'Selecionado' : 'Selecionar'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Informações adicionais */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          💳 Todos os planos incluem 30 dias grátis para teste
        </p>
        <p className="text-sm text-gray-500 mt-2">
          🔒 Pagamento seguro via Asaas (PIX, Cartão)
        </p>
        <p className="text-sm text-gray-500 mt-2">
          📊 Valores: Teste R$ 6,00 • Básico R$ 1.164/ano • Profissional R$ 3.564/ano • Enterprise R$ 5.964/ano
        </p>
        <p className="text-sm text-gray-500 mt-2">
          ⚡ Parcelamento disponível em até 12x sem juros no cartão
        </p>
      </div>

      {/* Opções de parcelamento */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          💳 Opções de Pagamento
        </h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <div className="font-medium text-gray-900">PIX</div>
            <div className="text-gray-600">Desconto de 5%</div>
          </div>
          <div className="text-center">
            <div className="font-medium text-gray-900">Cartão de Crédito</div>
            <div className="text-gray-600">Até 12x sem juros</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanoSelector;
